import { useState, useMemo } from "react";
import { usePersistedState } from "../../hooks/usePersistedState";
import { useGetAllExercisesQuery } from "../../libs/exercises/api";
import { useGetUserQuery } from "../../libs/user/api";
import { useGetSpecialCategoriesQuery } from "../../libs/specialCategory/api";
import { Workout } from "../../libs/workout/types";
import { BodyMuscleMap } from "./BodyMuscleMap";
import { getMuscleColor, getMuscleColorVolume, DISTRICT_TO_SLUGS, DISTRICT_DISPLAY_ORDER, DISTRICT_HIERARCHY } from "./muscleConfig";
import { getSetWeekAmountForBodyDistrict, getWeekFrequencyForBodyDistrict, getSetWeekAmountForSpecialCategory, buildExercisesLookup, getTonnageByDistrictAndExercise, getTopSetByDistrictAndExercise } from "./muscleUtils";
import { MuscleVolumeChart } from "./MuscleVolumeChart";
import { TonnageChart, EXERCISE_COLORS } from "./TonnageChart";
import { TopSetChart } from "./TopSetChart";
import { DistrictDropdown } from "./DistrictDropdown";

type MuscleSummaryTableProps = {
  workout: Workout;
  weeksColumns: string[];
  userId?: number;
};

const hasBodyMapping = (district: string): boolean => {
  return !!DISTRICT_TO_SLUGS[district];
};

export const MuscleSummaryTable: React.FC<MuscleSummaryTableProps> = ({
  workout,
  weeksColumns,
  userId,
}) => {
  const [hoveredDistrict, setHoveredDistrict] = useState<string | null>(null);
  const [chartActiveDistricts, setChartActiveDistricts] = usePersistedState<string[]>(`fitshape:chart:${userId}:volumeDistricts`, []);
  const [tonnageSelectedDistricts, setTonnageSelectedDistricts] = usePersistedState<string[]>(`fitshape:chart:${userId}:tonnageDistricts`, []);
  const { data: exercises, isLoading, error } = useGetAllExercisesQuery();
  const { data: specialCategories } = useGetSpecialCategoriesQuery();
  const { data: user } = useGetUserQuery(
    userId != null ? userId + "" : "",
    { skip: userId == null }
  );

  if (isLoading) {
    return <></>;
  }
  if (error || !exercises || !workout || !workout.data || !workout.data[0]) {
    return <></>;
  }

  // Lookup O(1) per trovare muscleDistribution/specialCategoryId per ID esercizio
  const exercisesLookup = buildExercisesLookup(exercises);

  // Estrai distretti unici da muscleDistribution degli esercizi
  const allDistricts: string[] = [];
  exercises.forEach((ex) => {
    if (!ex.muscleDistribution) return;
    for (const md of ex.muscleDistribution) {
      if (md.district && allDistricts.indexOf(md.district) === -1) {
        allDistricts.push(md.district);
      }
    }
  });

  const bodyDistrictsUnsorted = allDistricts.filter(hasBodyMapping);
  const bodyDistricts = [...bodyDistrictsUnsorted].sort((a, b) => {
    const ia = DISTRICT_DISPLAY_ORDER.indexOf(a);
    const ib = DISTRICT_DISPLAY_ORDER.indexOf(b);
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });
  const otherDistricts = allDistricts.filter((d) => !hasBodyMapping(d));
  // Escludi i distretti generici se ci sono gli specifici
  const allMappedDistricts = Object.keys(DISTRICT_TO_SLUGS).filter((d) => {
    // Se SPALLE ANTERIORI/MID/POSTERIORI esistono, nascondi SPALLE generico
    if (d === "SPALLE") {
      const hasSpecific = ["SPALLE ANTERIORI", "SPALLE MID", "SPALLE POSTERIORI"].some(
        (s) => DISTRICT_TO_SLUGS[s]
      );
      if (hasSpecific) return false;
    }
    // Se GLUTEI MAX/SIDE esistono, nascondi GLUTEI generico
    if (d === "GLUTEI") {
      const hasSpecific = ["GLUTEI MAX", "GLUTEI SIDE"].some(
        (s) => DISTRICT_TO_SLUGS[s]
      );
      if (hasSpecific) return false;
    }
    // Se PETTORALI UPPER/LOWER esistono, nascondi PETTORALI generico
    if (d === "PETTORALI") {
      const hasSpecific = ["PETTORALI UPPER", "PETTORALI LOWER"].some(
        (s) => DISTRICT_TO_SLUGS[s]
      );
      if (hasSpecific) return false;
    }
    // Se LOWER BACK esiste, nascondi DORSALI legacy
    if (d === "DORSALI") {
      if (DISTRICT_TO_SLUGS["LOWER BACK"]) return false;
    }
    // Se HYBRID UPPER BACK esiste, nascondi TRAPEZIO legacy
    if (d === "TRAPEZIO") {
      if (DISTRICT_TO_SLUGS["HYBRID UPPER BACK"]) return false;
    }
    return true;
  });

  const gender = user?.gender === "F" ? "female" : "male";

  // Toggle distretto: se attivo lo disattiva, se disattivo lo attiva
  const toggleChartDistrict = (district: string) => {
    setChartActiveDistricts((prev) =>
      prev.includes(district)
        ? prev.filter((d) => d !== district)
        : [...prev, district]
    );
  };

  return (
    <>
      {/* ══════════════════════════════════════════════════════════
          CARD 1 — Volume Muscolare
          ══════════════════════════════════════════════════════════ */}
      <div className="bg-gray-900 dark:bg-gray-800 dark:border dark:border-gray-700 rounded-2xl p-6 mt-8">
        <h2 className="text-lg font-bold text-gray-100 mb-6">Volume Muscolare</h2>

        {/* Corpo + tabella muscoli mappati */}
        <div className="flex flex-col lg:flex-row gap-6 mb-8 items-start">
          <div className="w-full lg:w-[40%] flex justify-center lg:self-start lg:sticky lg:top-20">
            <BodyMuscleMap
              activeDistricts={allMappedDistricts}
              hoveredDistrict={hoveredDistrict}
              onHoverDistrict={setHoveredDistrict}
              gender={gender}
              colorFn={getMuscleColorVolume}
            />
          </div>

          <div className="w-full lg:w-[60%] min-w-0 overflow-x-auto sm:rounded-lg">
            <MuscleTable
              districts={bodyDistricts}
              workout={workout}
              weeksColumns={weeksColumns}
              hoveredDistrict={hoveredDistrict}
              setHoveredDistrict={setHoveredDistrict}
              showColorDot
              exercisesLookup={exercisesLookup}
              colorFn={getMuscleColorVolume}
            />
          </div>
        </div>

        {/* Grafico volume set — corpo a sinistra, grafico a destra */}
        <div className="flex flex-col lg:flex-row gap-6 my-8 items-center">
          <div className="w-full lg:w-[40%] flex justify-center">
            <BodyMuscleMap
              activeDistricts={chartActiveDistricts}
              hoveredDistrict={null}
              onHoverDistrict={() => {}}
              onClickDistrict={toggleChartDistrict}
              gender={gender}
              scale={1}
              uniqueColors
            />
          </div>

          <div className="w-full lg:w-[60%] min-w-0">
            <MuscleVolumeChart
              workout={workout}
              weeksColumns={weeksColumns}
              districts={bodyDistricts}
              visibleDistricts={chartActiveDistricts}
              onToggleDistrict={toggleChartDistrict}
              onSelectAll={() => setChartActiveDistricts([...bodyDistricts])}
              onReset={() => setChartActiveDistricts([])}
              exercisesLookup={exercisesLookup}
            />
          </div>
        </div>

        {/* Sub-sezione: Esercizi Speciali */}
        {specialCategories && specialCategories.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-800">
            <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Esercizi Speciali</h4>
            <div className="overflow-x-auto sm:rounded-lg">
              <table className="w-full text-sm text-gray-300 text-center">
                <thead>
                  <tr className="text-xs text-gray-400 uppercase bg-gray-800/50">
                    <th className="px-5 py-3 text-left">Categoria</th>
                    <th className="px-4 py-3">Freq.</th>
                    {weeksColumns.map((col) => (
                      <th key={col} className="px-4 py-3">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {specialCategories.map((cat) => {
                    const hasAny = weeksColumns.some(
                      (_, i) => getSetWeekAmountForSpecialCategory(workout, cat.id, cat.name, i, exercisesLookup) > 0
                    );
                    if (!hasAny) return null;
                    return (
                      <tr key={cat.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                        <td className="px-5 py-2 text-left font-medium text-gray-200">
                          {cat.name}
                        </td>
                        <td className="px-4 py-2">
                          {Math.max(
                            ...weeksColumns.map((_, i) =>
                              getSetWeekAmountForSpecialCategory(workout, cat.id, cat.name, i, exercisesLookup) > 0 ? 1 : 0
                            )
                          )}
                        </td>
                        {weeksColumns.map((_, i) => (
                          <td key={i} className="px-4 py-2">
                            {getSetWeekAmountForSpecialCategory(workout, cat.id, cat.name, i, exercisesLookup) || "—"}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════
          CARD 2 — Performance
          ══════════════════════════════════════════════════════════ */}
      <div className="bg-gray-900 dark:bg-gray-800 dark:border dark:border-gray-700 rounded-2xl p-6 mt-8">
        <h2 className="text-lg font-bold text-gray-100 mb-6">Performance</h2>

        <TonnageSection
          workout={workout}
          weeksColumns={weeksColumns}
          selectedDistricts={tonnageSelectedDistricts}
          onSelectionChange={setTonnageSelectedDistricts}
          exercisesLookup={exercisesLookup}
          gender={gender}
        />
      </div>
    </>
  );
};

// ── Componente tabella riusabile ──

type MuscleTableProps = {
  districts: string[];
  workout: Workout;
  weeksColumns: string[];
  hoveredDistrict: string | null;
  setHoveredDistrict: (d: string | null) => void;
  chartActiveDistricts?: string[];
  onToggleChart?: (district: string) => void;
  showColorDot: boolean;
  exercisesLookup: Map<string, any>;
  colorFn?: (district: string) => string;
};

const MuscleTable: React.FC<MuscleTableProps> = ({
  districts,
  workout,
  weeksColumns,
  hoveredDistrict,
  setHoveredDistrict,
  chartActiveDistricts,
  onToggleChart,
  showColorDot,
  exercisesLookup,
  colorFn: colorFnProp,
}) => {
  const getColor = colorFnProp || getMuscleColor;
  return (
  <table className="w-full text-sm text-gray-300 text-center">
    <thead>
      <tr className="text-xs text-gray-400 uppercase bg-gray-800/50">
        <th className="px-5 py-3 text-left">Muscolo</th>
        <th className="px-4 py-3">Freq.</th>
        {weeksColumns.map((column) => (
          <th key={column} className="px-4 py-3 text-center">
            Sets - {column}
          </th>
        ))}
        {onToggleChart && <th className="px-3 py-3 w-10"></th>}
      </tr>
    </thead>
    <tbody>
      {districts.map((district) => {
        const color = getColor(district);
        const isHovered = hoveredDistrict === district;
        const isInChart = chartActiveDistricts?.includes(district) ?? false;
        return (
          <tr
            key={district}
            className="border-b border-gray-800 cursor-default transition-all duration-150"
            style={{
              borderLeft: showColorDot ? `5px solid ${color}` : undefined,
              backgroundColor: showColorDot
                ? isHovered
                  ? `${color}30`
                  : `${color}08`
                : undefined,
            }}
            onMouseEnter={() => setHoveredDistrict(district)}
            onMouseLeave={() => setHoveredDistrict(null)}
          >
            <td className="px-5 py-2.5 text-left font-semibold text-gray-200 flex items-center gap-2">
              {showColorDot && (
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: color }}
                />
              )}
              {district}
            </td>
            <td className="px-4 py-2.5 font-medium">
              {getWeekFrequencyForBodyDistrict(workout, district, 0, exercisesLookup)}
            </td>
            {weeksColumns.map((_column, index) => (
              <td key={index} className="px-4 py-2.5 text-center">
                {getSetWeekAmountForBodyDistrict(workout, district, index, exercisesLookup)}
              </td>
            ))}
            {onToggleChart && (
              <td className="px-3 py-2.5 text-center">
                <button
                  onClick={() => onToggleChart(district)}
                  className={`w-5 h-5 rounded flex items-center justify-center transition-all ${
                    isInChart ? "bg-blue-500 text-white" : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                  }`}
                  title={isInChart ? "Rimuovi dal grafico" : "Aggiungi al grafico"}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
                    />
                  </svg>
                </button>
              </td>
            )}
          </tr>
        );
      })}
      {/* Riga Totale */}
      <tr className="border-t-2 border-gray-600 bg-gray-800/80 font-bold">
        <td className="px-5 py-2.5 text-left text-gray-100">
          Totale
        </td>
        <td className="px-4 py-2.5 font-bold text-gray-100">
          {districts.reduce(
            (sum, d) => sum + getWeekFrequencyForBodyDistrict(workout, d, 0, exercisesLookup),
            0
          )}
        </td>
        {weeksColumns.map((_col, index) => (
          <td key={index} className="px-4 py-2.5 text-center font-bold text-gray-100">
            {districts.reduce(
              (sum, d) => sum + getSetWeekAmountForBodyDistrict(workout, d, index, exercisesLookup),
              0
            )}
          </td>
        ))}
        {onToggleChart && <td className="px-3 py-2.5"></td>}
      </tr>
    </tbody>
  </table>
  );
};

// ── Sezione Tonnellaggio ──

type TonnageSectionProps = {
  workout: Workout;
  weeksColumns: string[];
  selectedDistricts: string[];
  onSelectionChange: (districts: string[]) => void;
  exercisesLookup: Map<string, any>;
  gender: "male" | "female";
};

const TonnageSection: React.FC<TonnageSectionProps> = ({
  workout,
  weeksColumns,
  selectedDistricts,
  onSelectionChange,
  exercisesLookup,
  gender,
}) => {
  // Calcola tonnellaggio per esercizio in base ai distretti selezionati
  const tonnageEntries = useMemo(
    () =>
      getTonnageByDistrictAndExercise(
        workout,
        selectedDistricts,
        weeksColumns.length,
        exercisesLookup
      ),
    [workout, selectedDistricts, weeksColumns.length, exercisesLookup]
  );

  // Calcola top set (1RM stimato) per esercizio
  const topSetEntries = useMemo(
    () =>
      getTopSetByDistrictAndExercise(
        workout,
        selectedDistricts,
        weeksColumns.length,
        exercisesLookup
      ),
    [workout, selectedDistricts, weeksColumns.length, exercisesLookup]
  );

  // Mappa exerciseId → colore dal tonnellaggio (ordinato) per sincronizzare col TopSet
  const exerciseColorMap = useMemo(() => {
    const sorted = [...tonnageEntries].sort((a, b) => {
      const totalA = a.tonnagePerWeek.reduce<number>((s, v) => s + (v ?? 0), 0);
      const totalB = b.tonnagePerWeek.reduce<number>((s, v) => s + (v ?? 0), 0);
      return totalB - totalA;
    });
    const map: Record<string, string> = {};
    sorted.forEach((entry, idx) => {
      map[entry.exerciseId] = EXERCISE_COLORS[idx % EXERCISE_COLORS.length];
    });
    return map;
  }, [tonnageEntries]);

  // Toggle distretto dal body SVG: cerca a quale gruppo appartiene e togglalo
  const handleBodyClick = (district: string) => {
    if (selectedDistricts.includes(district)) {
      onSelectionChange(selectedDistricts.filter((d) => d !== district));
    } else {
      onSelectionChange([...selectedDistricts, district]);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 items-start">
      {/* Colonna sinistra: dropdown + reset + corpo SVG, sticky allo scroll */}
      <div className="w-full lg:w-[25%] flex flex-col items-center gap-4 mt-6 lg:sticky lg:top-36 lg:self-start">
        <div className="flex items-center gap-2 w-full max-w-xs">
          <div className="flex-1 min-w-0">
            <DistrictDropdown
              selectedDistricts={selectedDistricts}
              onSelectionChange={onSelectionChange}
            />
          </div>
          {selectedDistricts.length > 0 && (
            <button
              onClick={() => onSelectionChange([])}
              className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-700 border border-gray-600 text-gray-400 hover:bg-gray-600 hover:text-gray-200 transition-colors flex items-center justify-center"
              title="Reset selezione"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <BodyMuscleMap
          activeDistricts={selectedDistricts}
          hoveredDistrict={null}
          onHoverDistrict={() => {}}
          onClickDistrict={handleBodyClick}
          gender={gender}
          scale={0.7}
          uniformColor="#EAB308"
        />
      </div>

      {/* Colonna destra: grafici impilati */}
      <div className="w-full lg:w-[75%] min-w-0 flex flex-col gap-6">
        <TonnageChart
          weeksColumns={weeksColumns}
          entries={tonnageEntries}
        />
        <TopSetChart
          weeksColumns={weeksColumns}
          entries={topSetEntries}
          exerciseColorMap={exerciseColorMap}
          hasSelection={selectedDistricts.length > 0}
        />
      </div>
    </div>
  );
};
